/**
 * The skills list's pager — client-side only.
 *
 * **It pages what is already loaded.** `GET /skills` returns every skill for the
 * organization in one response and the shared list is a pure proxy, so this is a
 * rendering limit, not a query one: it keeps a long list readable and keeps the DOM
 * small, and does nothing for the fetch. Server-side paging is tracked separately;
 * until it lands, "Page 1 of 40" means 40 pages already in memory.
 *
 * At a single page the **buttons** go and the line keeps only the total: a pager under a
 * list that fits is furniture, but the count is worth reading either way, and it is the one
 * thing in the footer that is always true.
 */
import { useLocale } from "../../i18n/LocaleProvider";
import { PageSizeSelect } from "./ListControls";
import { ChevronLeftIcon } from "./icons";

export function Pager({
  page,
  pageCount,
  total,
  counting,
  onPage,
}: {
  /** The clamped page — `paginate()`'s answer, never the raw state. */
  page: number;
  pageCount: number;
  /** Rows after the search, the filter and the folder — not the store's total. */
  total: number;
  /** What is being counted: skills, or "items" when folders share the list with them. */
  counting: "skills" | "items";
  onPage: (next: number) => void;
}) {
  const { t } = useLocale();

  /**
   * **One sentence: "Page 1 of 2 - total 10 folders"** (owner).
   *
   * The page and the total were two elements at opposite ends of the row for a while, and
   * they read as two unrelated numbers. Together they are one statement about the same
   * list. At a single page the page half has nothing to say and only the total is rendered,
   * so the number never disappears when a list gets short.
   */
  const label =
    pageCount > 1
      ? t(counting === "items" ? "pager.pagesItems" : "pager.pagesSkills", { page, pageCount, total })
      : t(counting === "items" ? "pager.totalItems" : "pager.totalSkills", { total });

  return (
    <nav className="skills-pager" aria-label={t("pager.label")}>
      {pageCount > 1 ? (
        <button
          type="button"
          className="settings-btn secondary is-small"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label={t("pager.prev")}
          title={t("pager.prev")}
        >
          <ChevronLeftIcon size={14} />
        </button>
      ) : null}
      {/* `aria-live` so a screen reader hears the page change: the buttons keep focus,
          so nothing else would announce that the list underneath them moved. */}
      <span className="skills-pager-label" aria-live="polite">{label}</span>
      {pageCount > 1 ? (
        <button
          type="button"
          className="settings-btn secondary is-small"
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
          aria-label={t("pager.next")}
          title={t("pager.next")}
        >
          {/* The same chevron, turned — there is no right-facing one in the set. */}
          <span className="pager-flip"><ChevronLeftIcon size={14} /></span>
        </button>
      ) : null}
    </nav>
  );
}

/**
 * The list's footer: the pager row, pinned under the scroll region so it does not scroll
 * away with the cards it describes.
 *
 * Centred on a narrow window and pushed to the right on a wide one (owner) — the CSS decides
 * that; this only has to be one row.
 */
export function ListFoot({
  page,
  pageCount,
  total,
  counting = "skills",
  perPage,
  onPerPage,
  onPage,
}: {
  page: number;
  pageCount: number;
  total: number;
  /** "items" while folders are listed beside the skills, skills everywhere else. */
  counting?: "skills" | "items";
  perPage: number;
  onPerPage: (next: number) => void;
  onPage: (next: number) => void;
}) {
  return (
    <div className="skills-foot">
      {/* Left of the pager, since it is the pager's own setting: how much of the list one
          press moves through. */}
      <PageSizeSelect value={perPage} onChange={onPerPage} />
      <Pager page={page} pageCount={pageCount} total={total} counting={counting} onPage={onPage} />
    </div>
  );
}
